import z from 'zod'

import { zodErrorParser } from '@/utils/zod'
import { AssetsResponseSchema, BaseEntitySchema, UserDetailsSchema } from './Common'
import { OrganizationMemberRoles, OrganizationUserResponseSchema, SystemUserRoles } from './Organization'
import { SubjectResponseSchema } from './Subject'

const AuthIdentityProviders = [
  'Google',
  'SignInWithApple',
  'Cognito',
] as const

export type AuthIdentityProvidersType = typeof AuthIdentityProviders[number]

export const UserResponseSchema = BaseEntitySchema.extend({
  assets: AssetsResponseSchema,
  details: UserDetailsSchema,
  email: z.string(),
  id: z.uuid(),
  isVerified: z.boolean(),
  isSuperAdmin: z.boolean(),
  sub: z.string().nullish(),
  verifiedProfile: z.object().nullish(),
  organization: OrganizationUserResponseSchema.array().nullish(),
  subject: SubjectResponseSchema.extend({
    group: z.uuid().nullish(),
    user: z.union([
      z.uuid(),
      BaseEntitySchema.extend({
        id: z.uuid(),
        email: z.string(),
        isVerified: z.boolean(),
        details: z.uuid()
      }).strict()
    ])
  }).strict().nullish()
}).strict()

const MeResponseSchema = z.object({
  user: UserResponseSchema,
  organizations: z.array(z.object({
    id: z.uuid(),
    name: z.string(),
    roles: z.enum(SystemUserRoles).array(),
  }).strict())
}).strict()

const OrgUserResponseSchema = z.object({
  organizationMembers: OrganizationUserResponseSchema.extend({
    organization: z.object({
      id: z.uuid(),
    }),
    role: BaseEntitySchema.extend({
      id: z.number().int(),
      type: z.enum(OrganizationMemberRoles),
    }),
  }).array(),
  organizationIndividual: BaseEntitySchema.extend({
    id: z.uuid(),
    subject: z.object({
      id: z.uuid(),
    }),
    organization: z.object({
      id: z.uuid(),
    }),
  }).strict().nullish(),
}).strict()

const UserAssignmentResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  assets: z.union([AssetsResponseSchema, z.array(z.any())]).nullish(),
  details: UserDetailsSchema,
  email: z.string(),
  isVerified: z.boolean(),
  subject: z.object({
    id: z.uuid(),
  }).strict()
}).strict()

const UserAuthIdentitySchema = z.object({
  dateCreated: z.string().nullish(),
  primary: z.boolean(),
  provider: z.enum(AuthIdentityProviders),
  providerUserId: z.string(),
}).strict()

export type UserResponse = z.infer<typeof UserResponseSchema>
export type MeResponse = z.infer<typeof MeResponseSchema>
export type OrgUserResponse = z.infer<typeof OrgUserResponseSchema>
export type UserAssignmentResponse = z.infer<typeof UserAssignmentResponseSchema>
export type UserAuthIdentity = z.infer<typeof UserAuthIdentitySchema>

export const zodUserResponseValidator = (
  data: UserResponse
) => zodErrorParser(
  'UserResponse',
  UserResponseSchema,
  data
)

export const zodMeResponseValidator = (
  data: MeResponse
) => zodErrorParser(
  'MeResponse',
  MeResponseSchema,
  data,
)

export const zodOrgUserResponseValidator = (
  data: OrgUserResponse
) => zodErrorParser(
  'OrgUserResponse',
  OrgUserResponseSchema,
  data,
)

export const zodUserAssignmentResponseValidator = (
  data: UserAssignmentResponse[]
) => zodErrorParser(
  'UserAssignmentResponse',
  UserAssignmentResponseSchema,
  data,
)

export const zodUserAuthIdentityValidator = (
  data: UserAuthIdentity[]
) => zodErrorParser(
  'UserAuthIdentity',
  UserAuthIdentitySchema,
  data,
)
